import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, } from 'react-router-dom'
import LoginPage from "./views/LoginPage.js";
import RootRedirect from "./views/RootRedirect.js";
import AuthLoading from './views/AuthLoading';
import { Provider } from "react-redux";
import ProtectedRoute from './views/ProtectedRoute';
import { FirebaseProvider, store } from "common";
import { FirebaseConfig } from './config/FirebaseConfig';
import { GoogleMapApiConfig } from './config/GoogleMapApiConfig';
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { buildMuiThemeOptions } from './theme/tokens';
import { useJsApiLoader } from '@react-google-maps/api';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { webClientId } from "config/ClientIds.js";
import { HelmetProvider } from "react-helmet-async";
import CircularLoading from './components/CircularLoading';

// Faz 4 — lazy-load all heavy routes (Skill: web-performance-audit)
const PrivacyPolicy        = lazy(() => import('./views/PrivacyPolicy'));
const AboutUs              = lazy(() => import('./views/AboutUs'));
const MyProfile            = lazy(() => import('./views/MyProfile'));
const BookingHistory       = lazy(() => import('./views/BookingHistory'));
const Dashboard            = lazy(() => import('./views/Dashboard'));
const CarTypes             = lazy(() => import('./views/CarTypes'));
const AddBookings          = lazy(() => import('./views/AddBookings'));
const Promos               = lazy(() => import('./views/Promos'));
const Users                = lazy(() => import('./views/Users'));
const CustomerDetails      = lazy(() => import('./views/CustomerDetails'));
const Notifications        = lazy(() => import('./views/Notifications'));
const Settings             = lazy(() => import('./views/Settings'));
const Complain             = lazy(() => import('./views/Complain'));
const AddMoney             = lazy(() => import('./views/AddMoney'));
const Withdraws            = lazy(() => import('./views/Withdraws'));
const AllReports           = lazy(() => import('./views/AllReports'));
const ContactUs            = lazy(() => import('./views/ContactUs'));
const UserWallet           = lazy(() => import('./views/UserWallet'));
const CarsList             = lazy(() => import('./views/CarsList'));
const TermCondition        = lazy(() => import('./views/TermCondition'));
const Sos                  = lazy(() => import('./views/Sos'));
const DriverDetails        = lazy(() => import('./views/DriverDetails'));
const AddRiders            = lazy(() => import('./views/AddRiders'));
const AddDriver            = lazy(() => import('./views/AddDriver'));
const AddFleetAdmin        = lazy(() => import('./views/AddFleetAdmin'));
const AddAdmin             = lazy(() => import('./views/AddAdmin'));
const ErrorView            = lazy(() => import('./views/Error'));
const BookingDetails       = lazy(() => import('./views/BookingDetails'));
const UserDocuments        = lazy(() => import('./views/UserDocuments'));
const AddNotifications     = lazy(() => import('./views/AddNotifications'));
const AddCar               = lazy(() => import('./views/AddCar'));
const UpdateFleetAdmin     = lazy(() => import('./views/UpdateFleetAdmin'));
const UpdateAdmin          = lazy(() => import('./views/UpdateAdmin'));
const AddCarType           = lazy(() => import('./views/AddCarType'));
const UpdateCar            = lazy(() => import('./views/UpdateCar'));
const UpdateCarType        = lazy(() => import('./views/UpdateCarType'));
const CsAyarlarPage        = lazy(() => import('./views/cs/CsAyarlarPage'));
const CsSiparisEklePage    = lazy(() => import('./views/cs/CsSiparisEklePage'));

const libraries = ['geometry','drawing','places'];

i18n
.use(initReactI18next)
.init({
    resources: {},
    fallbackLng: "en",
    ns: ["translations"],
    defaultNS: "translations",
    interpolation: {
        escapeValue: false
    }
});

function App() {

  useJsApiLoader({
    id: 'google-map',
    googleMapsApiKey: GoogleMapApiConfig,
    libraries
  })

  // Kuryo rebrand (Faz 3) — createTheme beslemesi src/theme/tokens.js'ten gelir.
  const theme = createTheme(buildMuiThemeOptions());

  return (
    <HelmetProvider>
    <Provider store={store}>
      <FirebaseProvider config={FirebaseConfig}>
        <GoogleOAuthProvider clientId={webClientId}>
        <ThemeProvider theme={theme}>
          <AuthLoading>
           <BrowserRouter>
              <Suspense fallback={<CircularLoading />}>
              <Routes>
                <Route path="/dashboard" element={<ProtectedRoute permit={"admin,fleetadmin"}><Dashboard /></ProtectedRoute>}/>
                <Route path="/bookings" element={<ProtectedRoute permit={"customer,admin,driver,fleetadmin"}><BookingHistory /></ProtectedRoute>}/>
                <Route path="/bookings/bookingdetails/:id" element={<ProtectedRoute permit={"customer,admin,driver,fleetadmin"}><BookingDetails /></ProtectedRoute>}/>
                <Route path="/profile" element={<ProtectedRoute permit={"customer,admin,driver,fleetadmin"}><MyProfile /></ProtectedRoute>}/>
                <Route path="/cartypes" element={<ProtectedRoute permit={"admin"}><CarTypes /></ProtectedRoute>}/>
                <Route path="/cars" element={<ProtectedRoute permit={"admin,fleetadmin,driver"}><CarsList /></ProtectedRoute>}/>
                <Route path="/addbookings" element={<ProtectedRoute permit={"admin,fleetadmin,customer"}><AddBookings /></ProtectedRoute>}/>
                <Route path="/promos" element={<ProtectedRoute permit={"admin"}><Promos /></ProtectedRoute>}/>
                <Route path="/users" element={<ProtectedRoute permit={"admin,fleetadmin"}><Users /></ProtectedRoute>}/>
                <Route path="/users/customerdetails/:id" element={<ProtectedRoute permit={"admin,fleetadmin"}><CustomerDetails/></ProtectedRoute>} errorElement={<ErrorView/>}/>
                <Route path="/users/driverdetails/:id" element={<ProtectedRoute permit={"admin,fleetadmin"}><DriverDetails/></ProtectedRoute>} errorElement={<ErrorView/>}/>
                <Route path="/users/userdocuments/:id" element={<ProtectedRoute permit={"admin,fleetadmin"}><UserDocuments/></ProtectedRoute>} errorElement={<ErrorView/>}/>
                <Route path="/users/fleetadminupdate/:id" element={<ProtectedRoute permit={"admin,fleetadmin"}><UpdateFleetAdmin/></ProtectedRoute>} errorElement={<ErrorView/>}/>
                <Route path="/users/adminupdate/:id" element={<ProtectedRoute permit={"admin,fleetadmin"}><UpdateAdmin/></ProtectedRoute>} errorElement={<ErrorView/>}/>
                <Route path="/notifications" element={<ProtectedRoute permit={"admin"}><Notifications /></ProtectedRoute>}/>
                <Route path="/addtowallet" element={<ProtectedRoute permit={"admin"}><AddMoney /></ProtectedRoute>}/>
                <Route path="/userwallet" element={<ProtectedRoute permit={"customer,driver"}><UserWallet /></ProtectedRoute>}/>
                <Route path="/withdraws" element={<ProtectedRoute permit={"admin"}><Withdraws /></ProtectedRoute>}/>
                <Route path="/sos" element={<ProtectedRoute permit={"admin"}><Sos /></ProtectedRoute>}/>
                <Route path="/complain" element={<ProtectedRoute permit={"admin"}><Complain /></ProtectedRoute>}/>
                <Route path="/allreports" element={<ProtectedRoute permit={"admin,fleetadmin"}><AllReports /></ProtectedRoute>}/>
                <Route path="/settings" element={<ProtectedRoute permit={"admin"}><Settings /></ProtectedRoute>}/>
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/term-condition" element={<TermCondition />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/users/addrider" element={<ProtectedRoute permit={"admin,fleetadmin"}><AddRiders/></ProtectedRoute>}/>
                <Route path="/users/adddriver" element={<ProtectedRoute permit={"admin,fleetadmin"}><AddDriver/></ProtectedRoute>}/>
                <Route path="/users/addfleetadmin" element={<ProtectedRoute permit={"admin,fleetadmin"}><AddFleetAdmin/></ProtectedRoute>}/>
                <Route path="/users/addadmin" element={<ProtectedRoute permit={"admin"}><AddAdmin/></ProtectedRoute>}/>
                <Route path="/notifications/addnotifications" element={<ProtectedRoute permit={"admin"}><AddNotifications/></ProtectedRoute>}/>
                <Route path="/cars/addcar" element={<ProtectedRoute permit={"admin,fleetadmin, driver"}><AddCar/></ProtectedRoute>}/>
                <Route path="/cars/updatecar/:id" element={<ProtectedRoute permit={"admin,fleetadmin, driver"}><UpdateCar/></ProtectedRoute>}/>
                <Route path="/cartypes/addcartype" element={<ProtectedRoute permit={"admin"}><AddCarType/></ProtectedRoute>}/>
                <Route path="/cartypes/updatecartypes/:id" element={<ProtectedRoute permit={"admin"}><UpdateCarType/></ProtectedRoute>}/>
                <Route path="/cs/ayarlar" element={<ProtectedRoute permit={"admin,fleetadmin,customer"}><CsAyarlarPage/></ProtectedRoute>}/>
                <Route path="/cs/siparis-ekle" element={<ProtectedRoute permit={"customer,admin,fleetadmin"}><CsSiparisEklePage/></ProtectedRoute>}/>
                <Route path="/" element={<RootRedirect />} />
                <Route path="/*" element={<ErrorView/>} />
              </Routes>
              </Suspense>
            </BrowserRouter>
          </AuthLoading>
        </ThemeProvider>
        </GoogleOAuthProvider>
      </FirebaseProvider>
    </Provider>
    </HelmetProvider>
  );
}

export default App;
